import { getTeams } from "core-integration/src/store/teams/reducers";
import { TFunction } from "i18next";
import { FormSelect, FormTextField } from "plos/src/components/forms";
import { Select } from "plos/src/components/forms/ReactHookFormControlled/Select";
import { contentMain } from "plos/src/layouts";
import { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOnboardingContext } from "../../../../../../contexts/OnboardingContext";
import { onboardingHeading } from "../../../onboarding.css";
import {
  BudgetAllocationChoice,
  PositionFocusChoice,
  PreferFavouriteTeamChoice,
  TeamBuilderPreferencesFormData,
} from "../../../types";
import {
  ExtendedLabelFormOptions,
  getBudgetAllocationChoices,
  getPositionFocusChoices,
  getPreferFavouriteTeamChoices,
} from "../../../utils";
import { EditableSummaryItem } from "../../EditableSummaryItem";
import { summaryList } from "../../teamBuilder.css";

type PreferenceField = keyof TeamBuilderPreferencesFormData;
type EditableField = "name" | "favourite_team" | PreferenceField;

// Resolve the display label for a stored choice value
const getSummaryLabel = (
  stepChoices: ExtendedLabelFormOptions[],
  t: TFunction<"onboarding">,
  fieldValue?: string,
) => {
  const choice = stepChoices.find((option) => option.value === fieldValue);
  return choice ? choice.label : t("notSet", "Not set");
};

const TeamBuilderSummary = () => {
  const { entryDetails, updateEntryDetails } = useOnboardingContext();
  const { control, setValue, getValues, watch } =
    useFormContext<TeamBuilderPreferencesFormData>();
  const { t } = useTranslation("onboarding");
  const teams = useSelector(getTeams);

  // Only one row can be edited at a time.
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [nameDraft, setNameDraft] = useState("");
  const [favouriteDraft, setFavouriteDraft] = useState("");
  // The preference value before an edit began, so Cancel can restore it.
  const previousPreference = useRef<
    | PreferFavouriteTeamChoice
    | BudgetAllocationChoice
    | PositionFocusChoice
    | undefined
  >(undefined);

  const favouriteTeam = entryDetails?.favourite_team ?? null;
  const { preferFavouriteTeam, budgetAllocation, positionFocus } = watch();

  const teamNameLabel = t("teamName.heading", "Team Name");
  const favouriteTeamHeading = t("favouriteTeam.heading", "Favourite Club");

  const favouriteTeamLabel =
    Number(favouriteTeam) === -1
      ? t("favouriteTeam.none", "None")
      : (teams.find((team) => team.id === Number(favouriteTeam))?.name ??
        t("notSet", "Not set"));

  const preferenceFields = [
    // Don't show the favourite club preference if the user has no favourite club
    ...(Number(favouriteTeam) !== -1
      ? [
          {
            name: "preferFavouriteTeam" as const,
            label: t(
              "preferFavouriteTeamPlayers",
              "Prefer Favourite Club Players",
            ),
            value: preferFavouriteTeam,
            choices: getPreferFavouriteTeamChoices(t),
          },
        ]
      : []),
    {
      name: "budgetAllocation",
      label: t("budgetAllocation", "Budget Allocation"),
      value: budgetAllocation,
      choices: getBudgetAllocationChoices(t),
    },
    {
      name: "positionFocus",
      label: t("positionFocus", "Position Focus"),
      value: positionFocus,
      choices: getPositionFocusChoices(t),
    },
  ] as const;

  const startEditName = () => {
    setNameDraft(entryDetails?.name ?? "");
    setEditingField("name");
  };

  const saveName = () => {
    updateEntryDetails({ name: nameDraft.trim() });
    setEditingField(null);
  };

  const startEditFavourite = () => {
    setFavouriteDraft(String(favouriteTeam ?? ""));
    setEditingField("favourite_team");
  };

  const saveFavourite = () => {
    const favourite = Number(favouriteDraft);
    updateEntryDetails({ favourite_team: favourite });
    // Clear the favourite-club preference when there is no favourite club.
    if (favourite === -1) {
      setValue("preferFavouriteTeam", undefined);
    }
    setEditingField(null);
  };

  const startEditPreference = (field: PreferenceField) => {
    previousPreference.current = getValues(field);
    setEditingField(field);
  };

  const cancelPreference = (field: PreferenceField) => {
    // Restore the value captured when editing started.
    setValue(field, previousPreference.current as never);
    setEditingField(null);
  };

  return (
    <div className={contentMain}>
      <div className={summaryList}>
        <EditableSummaryItem
          label={teamNameLabel}
          value={entryDetails?.name ?? t("notSet", "Not set")}
          isEditing={editingField === "name"}
          onEdit={startEditName}
          onSave={saveName}
          onCancel={() => setEditingField(null)}
        >
          <FormTextField
            label={teamNameLabel}
            placeholder={t("teamName.placeholder", "Enter Name Here")}
            value={nameDraft}
            onChange={setNameDraft}
            maxLength={20}
            autoFocus
          />
        </EditableSummaryItem>

        <EditableSummaryItem
          label={favouriteTeamHeading}
          value={favouriteTeamLabel}
          isEditing={editingField === "favourite_team"}
          onEdit={startEditFavourite}
          onSave={saveFavourite}
          onCancel={() => setEditingField(null)}
        >
          <FormSelect
            id="favourite-club"
            label={favouriteTeamHeading}
            value={favouriteDraft}
            onChange={(e) => setFavouriteDraft(e.target.value)}
            autoFocus
          >
            {teams.map(({ id, name: teamName }) => (
              <option key={id} value={id}>
                {teamName}
              </option>
            ))}
            <option key="none" value={-1}>
              {t("favouriteTeam.none", "None")}
            </option>
          </FormSelect>
        </EditableSummaryItem>
      </div>

      <h2 className={onboardingHeading}>
        {t("autopickPreferences", "Autopick Preferences")}
      </h2>

      <div className={summaryList}>
        {preferenceFields.map(({ name, label, value, choices }) => (
          <EditableSummaryItem
            key={name}
            label={label}
            value={getSummaryLabel(choices, t, value)}
            isEditing={editingField === name}
            onEdit={() => startEditPreference(name)}
            onSave={() => setEditingField(null)}
            onCancel={() => cancelPreference(name)}
          >
            <Select
              control={control}
              name={name}
              id={name}
              label={label}
              autoFocus
            >
              {choices.map(
                ({
                  value: choiceValue,
                  label: choiceLabel,
                }: {
                  value:
                    | PreferFavouriteTeamChoice
                    | BudgetAllocationChoice
                    | PositionFocusChoice;
                  label: string;
                }) => (
                  <option key={choiceValue} value={choiceValue}>
                    {choiceLabel}
                  </option>
                ),
              )}
            </Select>
          </EditableSummaryItem>
        ))}
      </div>
    </div>
  );
};

export default TeamBuilderSummary;
